import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import pb, { apiCall } from '../services/pocketbase';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {
  FiAlertCircle,
  FiArchive,
  FiCheck,
  FiEdit3,
  FiEye,
  FiPlus,
  FiSearch,
  FiVideo,
  FiX
} = FiIcons;

const emptyFilters = {
  visibility_scope: '',
  workflow_status: '',
  category: '',
  distribution_channel: '',
  app_id: ''
};

const escapeFilterValue = (value) => String(value || '')
  .replace(/\\/g, '\\\\')
  .replace(/"/g, '\\"');

const Videos = () => {
  const [videos, setVideos] = useState([]);
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [filters, setFilters] = useState(emptyFilters);
  const [selectedVideos, setSelectedVideos] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    perPage: 20,
    totalItems: 0,
    totalPages: 0
  });

  useEffect(() => {
    loadApps();
  }, []);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [searchTerm]);

  useEffect(() => {
    loadVideos();
  }, [debouncedSearchTerm, filters, pagination.page]);

  const loadApps = async () => {
    try {
      const result = await apiCall(() => pb.collection('apps').getFullList({
        sort: 'app_name',
        filter: 'is_active = true'
      }));
      setApps(result);
    } catch (error) {
      console.error('Failed to load apps for video filters:', error);
    }
  };

  const buildFilter = () => {
    const clauses = ['workflow_status != "archived"'];

    if (debouncedSearchTerm) {
      const search = escapeFilterValue(debouncedSearchTerm);
      clauses.push(`(title ~ "${search}" || description ~ "${search}" || keywords ~ "${search}" || tags ~ "${search}" || category ~ "${search}")`);
    }

    if (filters.visibility_scope) {
      clauses.push(`visibility_scope = "${escapeFilterValue(filters.visibility_scope)}"`);
    }
    if (filters.workflow_status) {
      clauses.push(`workflow_status = "${escapeFilterValue(filters.workflow_status)}"`);
    }
    if (filters.category) {
      clauses.push(`category = "${escapeFilterValue(filters.category)}"`);
    }
    if (filters.distribution_channel) {
      clauses.push(`distribution_channels ?= "${escapeFilterValue(filters.distribution_channel)}"`);
    }
    if (filters.app_id) {
      clauses.push(`app_ids ?= "${escapeFilterValue(filters.app_id)}"`);
    }

    return clauses.join(' && ');
  };

  const loadVideos = async () => {
    try {
      setLoading(true);
      setLoadError('');

      const result = await apiCall(() => pb.collection('videos').getList(
        pagination.page,
        pagination.perPage,
        {
          filter: buildFilter(),
          sort: '-updated',
          expand: 'app_ids'
        }
      ));

      setVideos(result.items);
      setPagination(prev => ({
        ...prev,
        totalItems: result.totalItems,
        totalPages: result.totalPages
      }));

      setSelectedVideos(current => {
        const visibleIds = new Set(result.items.map(video => video.id));
        return new Set(Array.from(current).filter(id => visibleIds.has(id)));
      });
    } catch (error) {
      console.error('Failed to load videos:', error);
      setVideos([]);
      setSelectedVideos(new Set());
      setLoadError('Could not load videos. Confirm PocketBase access and try again.');
    } finally {
      setLoading(false);
    }
  };

  const updateFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
    setSelectedVideos(new Set());
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setPagination(prev => ({ ...prev, page: 1 }));
    setSelectedVideos(new Set());
  };

  const toggleVideo = (videoId) => {
    setSelectedVideos(current => {
      const next = new Set(current);
      if (next.has(videoId)) next.delete(videoId);
      else next.add(videoId);
      return next;
    });
  };

  const toggleAll = () => {
    if (videos.length > 0 && selectedVideos.size === videos.length) {
      setSelectedVideos(new Set());
      return;
    }
    setSelectedVideos(new Set(videos.map(video => video.id)));
  };

  const archiveVideo = async (video) => {
    if (!window.confirm(`Archive “${video.title}”?`)) return;

    try {
      setLoadError('');
      await apiCall(() => pb.collection('videos').update(video.id, { workflow_status: 'archived' }));
      await loadVideos();
    } catch (error) {
      console.error('Failed to archive video:', error);
      setLoadError('Could not archive this video. Please try again.');
    }
  };

  const archiveSelected = async () => {
    if (selectedVideos.size === 0) return;
    if (!window.confirm(`Archive ${selectedVideos.size} selected video(s)?`)) return;

    try {
      setBulkLoading(true);
      setLoadError('');
      await Promise.all(
        Array.from(selectedVideos).map(id =>
          apiCall(() => pb.collection('videos').update(id, { workflow_status: 'archived' }))
        )
      );
      setSelectedVideos(new Set());
      await loadVideos();
    } catch (error) {
      console.error('Failed to archive selected videos:', error);
      setLoadError('One or more selected videos could not be archived. Refresh and try again.');
    } finally {
      setBulkLoading(false);
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setFilters(emptyFilters);
    setPagination(prev => ({ ...prev, page: 1 }));
    setSelectedVideos(new Set());
  };

  const hasActiveFilters = useMemo(
    () => Boolean(searchTerm) || Object.values(filters).some(Boolean),
    [searchTerm, filters]
  );

  const statusBadge = (status) => {
    const classes = {
      draft: 'bg-slate-700 text-slate-300',
      review: 'bg-yellow-500/20 text-yellow-400',
      published: 'bg-green-500/20 text-green-400',
      archived: 'bg-red-500/20 text-red-400'
    };
    return classes[status] || classes.draft;
  };

  const filterControlClass = 'bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all placeholder-slate-400';

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Videos Management</h1>
          <p className="text-slate-400 mt-1">Manage training metadata, targeting, audience and publication status</p>
        </div>
        <Link
          to="/videos/new"
          className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg flex items-center justify-center space-x-2 transition-colors shadow-lg shadow-blue-900/20 whitespace-nowrap"
        >
          <SafeIcon icon={FiPlus} className="w-4 h-4" />
          <span>Add Video</span>
        </Link>
      </div>

      {loadError && (
        <div className="mb-4 bg-red-500/10 border border-red-500/20 text-red-200 px-4 py-3 rounded-xl flex items-start gap-2 text-sm">
          <SafeIcon icon={FiAlertCircle} className="w-5 h-5 flex-shrink-0" />
          <span>{loadError}</span>
        </div>
      )}

      <div className="bg-slate-900 rounded-xl border border-slate-800 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-800">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-7 gap-3">
            <div className="xl:col-span-2 relative">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search title, description, category, keywords or tags..."
                value={searchTerm}
                onChange={(event) => handleSearchChange(event.target.value)}
                className="pl-9 pr-4 py-2 w-full bg-slate-800 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all placeholder-slate-400 text-sm"
              />
            </div>

            <select value={filters.visibility_scope} onChange={(event) => updateFilter('visibility_scope', event.target.value)} className={filterControlClass}>
              <option value="">All Scopes</option>
              <option value="client_facing">Client Facing</option>
              <option value="bpas_internal_team">Internal Team</option>
            </select>

            <select value={filters.workflow_status} onChange={(event) => updateFilter('workflow_status', event.target.value)} className={filterControlClass}>
              <option value="">All Statuses</option>
              <option value="draft">Draft</option>
              <option value="review">Review</option>
              <option value="published">Published</option>
            </select>

            <select value={filters.category} onChange={(event) => updateFilter('category', event.target.value)} className={filterControlClass}>
              <option value="">All Categories</option>
              <option value="onboarding">Onboarding</option>
              <option value="feature">Feature</option>
              <option value="troubleshooting">Troubleshooting</option>
              <option value="sop">SOP</option>
            </select>

            <select value={filters.distribution_channel} onChange={(event) => updateFilter('distribution_channel', event.target.value)} className={filterControlClass}>
              <option value="">All Channels</option>
              <option value="in_app_training_page">In-App Training</option>
              <option value="support_resolution">Support Resolution</option>
              <option value="onboarding_modal">Onboarding Modal</option>
              <option value="internal_sop_library">Internal SOP Library</option>
            </select>

            <div className="flex gap-2 min-w-0">
              <select value={filters.app_id} onChange={(event) => updateFilter('app_id', event.target.value)} className={`flex-1 min-w-0 ${filterControlClass}`}>
                <option value="">All Apps</option>
                {apps.map(app => <option key={app.id} value={app.id}>{app.app_name}</option>)}
              </select>
              {hasActiveFilters && (
                <button type="button" onClick={clearFilters} className="px-3 py-2 text-slate-400 hover:text-white border border-slate-600 rounded-lg hover:bg-slate-700 transition-colors" title="Clear filters">
                  <SafeIcon icon={FiX} className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>

        {selectedVideos.size > 0 && (
          <div className="px-4 py-3 border-b border-slate-800 bg-blue-500/10 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-blue-200 font-medium text-sm">
              <SafeIcon icon={FiCheck} className="w-4 h-4 text-blue-400" />
              {selectedVideos.size} video(s) selected
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={archiveSelected}
                disabled={bulkLoading}
                className="bg-red-500/20 text-red-400 border border-red-500/30 px-4 py-2 rounded-lg hover:bg-red-500/30 disabled:opacity-50 flex items-center gap-2 transition-colors text-sm"
              >
                <SafeIcon icon={FiArchive} className="w-4 h-4" />
                {bulkLoading ? 'Archiving...' : 'Archive Selected'}
              </button>
              <button type="button" onClick={() => setSelectedVideos(new Set())} className="text-slate-400 hover:text-white text-sm">Cancel</button>
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-800">
            <thead>
              <tr>
                <th className="px-6 py-4 text-left">
                  <input type="checkbox" checked={videos.length > 0 && selectedVideos.size === videos.length} onChange={toggleAll} className="h-4 w-4 bg-slate-700 border-slate-600 rounded text-blue-600" />
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Video</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Scope</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Apps</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Updated</th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-slate-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800 bg-slate-900/50">
              {loading ? (
                [...Array(5)].map((_, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4"><div className="h-4 w-4 rounded bg-slate-800 animate-pulse" /></td>
                    <td className="px-6 py-4"><div className="h-14 w-64 rounded bg-slate-800 animate-pulse" /></td>
                    <td className="px-6 py-4"><div className="h-6 w-20 rounded bg-slate-800 animate-pulse" /></td>
                    <td className="px-6 py-4"><div className="h-6 w-20 rounded bg-slate-800 animate-pulse" /></td>
                    <td className="px-6 py-4"><div className="h-6 w-24 rounded bg-slate-800 animate-pulse" /></td>
                    <td className="px-6 py-4"><div className="h-4 w-24 rounded bg-slate-800 animate-pulse" /></td>
                    <td className="px-6 py-4"><div className="h-8 w-24 rounded bg-slate-800 animate-pulse ml-auto" /></td>
                  </tr>
                ))
              ) : videos.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-14 text-center">
                    <SafeIcon icon={FiVideo} className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                    <p className="text-slate-400 font-medium">No videos found</p>
                    <p className="text-slate-600 text-sm mt-1">Change the filters or add your first training video.</p>
                  </td>
                </tr>
              ) : (
                videos.map(video => (
                  <tr key={video.id}>
                    <td className="px-6 py-4">
                      <input type="checkbox" checked={selectedVideos.has(video.id)} onChange={() => toggleVideo(video.id)} className="h-4 w-4 bg-slate-700 border-slate-600 rounded text-blue-600" />
                    </td>
                    <td className="px-6 py-4 min-w-[360px] max-w-lg">
                      <div className="flex items-start gap-4">
                        <div className="w-24 aspect-video rounded-xl bg-slate-800 overflow-hidden flex-shrink-0 border border-slate-700 shadow-sm">
                          {video.thumbnail_url ? <img src={video.thumbnail_url} alt={`${video.title || 'Training video'} thumbnail`} className="w-full h-full object-cover" loading="lazy" /> : <div className="w-full h-full flex items-center justify-center"><SafeIcon icon={FiVideo} className="w-5 h-5 text-slate-600" /></div>}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-white truncate">{video.title}</p>
                          <p className="text-xs text-slate-500 mt-1 leading-5 line-clamp-2">{video.description || 'No description added yet.'}</p>
                          <p className="text-[10px] text-slate-500 uppercase tracking-wider mt-1.5">{String(video.category || 'training').replace(/_/g, ' ')}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${video.visibility_scope === 'client_facing' ? 'bg-blue-500/10 text-blue-400' : 'bg-purple-500/10 text-purple-400'}`}>
                        {video.visibility_scope === 'client_facing' ? 'Client' : 'Internal'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium uppercase tracking-wider ${statusBadge(video.workflow_status)}`}>{video.workflow_status}</span>
                    </td>
                    <td className="px-6 py-4 max-w-xs">
                      {video.expand?.app_ids?.length ? (
                        <div className="flex flex-wrap gap-1">
                          {video.expand.app_ids.slice(0, 2).map(app => <span key={app.id} className="bg-slate-800 border border-slate-700 text-blue-300 px-2 py-1 rounded text-xs">{app.app_name}</span>)}
                          {video.expand.app_ids.length > 2 && <span className="text-xs text-slate-500 py-1">+{video.expand.app_ids.length - 2}</span>}
                        </div>
                      ) : <span className="text-xs text-slate-600">Not targeted</span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-xs text-slate-500">
                      {video.updated ? format(new Date(video.updated), 'dd MMM yyyy, HH:mm') : '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex justify-end gap-1">
                        <Link to={`/videos/${video.id}`} className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg" title="View video details"><SafeIcon icon={FiEye} className="w-4 h-4" /></Link>
                        <Link to={`/videos/${video.id}/edit`} className="p-2 text-blue-400 hover:text-white hover:bg-blue-600/20 rounded-lg" title="Edit video"><SafeIcon icon={FiEdit3} className="w-4 h-4" /></Link>
                        <button type="button" onClick={() => archiveVideo(video)} className="p-2 text-red-400 hover:text-white hover:bg-red-600/20 rounded-lg" title="Archive video"><SafeIcon icon={FiArchive} className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {!loading && pagination.totalPages > 1 && (
          <div className="px-4 py-3 border-t border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-sm">
            <p className="text-slate-500">{pagination.totalItems} video(s)</p>
            <div className="flex items-center gap-2">
              <button type="button" disabled={pagination.page <= 1} onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))} className="px-3 py-2 rounded-lg bg-slate-800 text-slate-300 disabled:opacity-40 hover:bg-slate-700">Previous</button>
              <span className="text-slate-400 px-2">Page {pagination.page} of {pagination.totalPages}</span>
              <button type="button" disabled={pagination.page >= pagination.totalPages} onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))} className="px-3 py-2 rounded-lg bg-slate-800 text-slate-300 disabled:opacity-40 hover:bg-slate-700">Next</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Videos;